import { minutesToMilliseconds } from 'date-fns';
import { match } from 'ts-pattern';
import { z } from 'zod';

const configSchema = z.object({
  apiBaseUrl: z.string().url(),
  apiArchiveBaseUrl: z.string().url(),
  apiTVBaseUrl: z.string().url(),
  apiWsUrl: z.string().url(),
  defaultSelectedTicker: z.string(),
  nodeEnv: z.enum(['development', 'production', 'test']).default('development'),
  isProd: z.boolean(),
  isDev: z.boolean(),
  walletConnectProjectId: z.string(),
  sentryDsn: z.string().url().optional(),
  sentryReplaysSessionSampleRate: z.coerce.number().optional().default(0.5),
  sentryReplaysOnErrorSampleRate: z.coerce.number().optional().default(1.0),
  sentryTracesSampleRate: z.coerce.number().optional().default(1.0),
  tvUpdateFrequency: z.coerce.number().optional().default(5000),
  tabInactiveThresholdMs: z.coerce.number().optional().default(minutesToMilliseconds(3)),
  downForMaintenance: z
    .union([
      z.literal('true').transform(() => true),
      z.literal('false').transform(() => false),
      z.boolean(),
    ])
    .optional()
    .default(false),
  wsInvalidationDelayMs: z.coerce.number().optional().default(50),
  useArchiveDb: z
    .union([
      z.literal('true').transform(() => true),
      z.literal('false').transform(() => false),
      z.boolean(),
    ])
    .optional()
    .default(true),
});

export type Config = z.infer<typeof configSchema>;

function loadConfig() {
  let bypassMaintenance = false;
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    bypassMaintenance = searchParams.get('bypassMaintenance') === 'true';
  }

  const parsed = configSchema.parse({
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    apiArchiveBaseUrl: import.meta.env.VITE_ARCHIVE_API_BASE_URL,
    apiTVBaseUrl: import.meta.env.VITE_TV_API_BASE_URL,
    apiWsUrl: import.meta.env.VITE_API_WS_URL,
    defaultSelectedTicker: import.meta.env.VITE_DEFAULT_SELECTED_TICKER,
    walletConnectProjectId: import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID,
    sentryDsn: import.meta.env.VITE_SENTRY_DSN,
    sentryReplaysSessionSampleRate: import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
    sentryReplaysOnErrorSampleRate: import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
    sentryTracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    tvUpdateFrequency: import.meta.env.VITE_TRADING_VIEW_UPDATE_FREQUENCY,
    tabInactiveThresholdMs: import.meta.env.VITE_TV_TAB_INACTIVE_THRESHOLD_MS,
    wsInvalidationDelayMs: import.meta.env.VITE_WS_INVALIDATION_DELAY_MS,
    downForMaintenance: bypassMaintenance ? false : import.meta.env.VITE_DOWN_FOR_MAINTENANCE,
    useArchiveDb: import.meta.env.VITE_USE_ARCHIVE_DB,
    nodeEnv: import.meta.env.MODE,
    isProd: import.meta.env.PROD,
    isDev: import.meta.env.DEV,
  });

  return {
    ...parsed,
    environment: match(parsed.apiBaseUrl)
      .returnType<'mainnet' | 'testnet' | 'devnet' | 'development'>()
      .with('https://api.ethereal.trade', () => 'mainnet')
      .with('https://api.etherealtest.net', () => 'testnet')
      .with('https://api.etherealdev.net', () => 'devnet')
      .otherwise(() => 'development'),
  };
}

export const config = loadConfig();
export const {
  apiBaseUrl,
  apiArchiveBaseUrl,
  apiTVBaseUrl,
  nodeEnv,
  isProd,
  isDev,
  defaultSelectedTicker,
  walletConnectProjectId,
  apiWsUrl,
  sentryReplaysOnErrorSampleRate,
  sentryReplaysSessionSampleRate,
  sentryTracesSampleRate,
  downForMaintenance,
  environment,
  sentryDsn,
  tvUpdateFrequency,
  tabInactiveThresholdMs,
  wsInvalidationDelayMs,
  useArchiveDb,
} = config;
